import { d2r, r2d } from "../../../util/functions/math";
import type { BasicAngle } from "./BasicAngle";
import type { ICoordinateSpace } from "./CoordinateSpace";
import type { IVector } from "./Vector";

export class Angle {
	/**
	 * A number between 0 and 2 * Math.PI
	 */
	public readonly radians: number;

	/**
	 * A number between 0 and 360
	 */
	public readonly degrees: number;

	private constructor(radians: number, degrees: number) {
		this.radians = radians;
		this.degrees = degrees;
	}

	normalize(): Angle {
		const { degrees } = this;

		if (degrees < 0) {
			return Angle.fromDegrees((degrees % 360) + 360);
		}
		if (degrees >= 360) {
			return Angle.fromDegrees(degrees % 360);
		}
		return this;
	}

	negate(): Angle {
		const { degrees } = this;
		return Angle.fromDegrees(360 - degrees);
	}

	addAngle(angle: Angle): Angle {
		return this.addDegrees(angle.degrees);
	}

	subtractAngle(angle: Angle): Angle {
		return this.addDegrees(-angle.degrees);
	}

	addDegrees(degrees: number): Angle {
		return Angle.fromDegrees(this.degrees + degrees);
	}

	addRadians(radians: number): Angle {
		return Angle.fromRadians(this.radians + radians);
	}

	toBasicAngle(): BasicAngle {
		return { radians: this.radians };
	}

	toString() {
		const { degrees } = this;

		return `${degrees.toFixed(1)} °`;
	}

	static fromRadians(radians: number) {
		const degrees = r2d(radians);
		return new Angle(radians, degrees);
	}

	static fromDegrees(degrees: number) {
		const radians = d2r(degrees);
		return new Angle(radians, degrees);
	}

	static fromBasicAngle(basicAngle: BasicAngle) {
		return Angle.fromRadians(basicAngle.radians);
	}

	abs(): Angle {
		const { degrees } = this;
		return Angle.fromDegrees(Math.abs(degrees));
	}

	equals(angle: Angle) {
		return this.degrees === angle.degrees;
	}

	static betweenTwoVectors<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(vector1: IVector<TCoordinateSpace>, vector2: IVector<TCoordinateSpace>) {
		// computes the angle that is made by vector 1 relative to the X-axis
		const vector1Angle = Math.atan2(vector1.y, vector1.x);
		// computes the angle that is made by vector 2 relative to the X-axis
		const vector2Angle = Math.atan2(vector2.y, vector2.x);

		return Angle.fromRadians(vector2Angle - vector1Angle);
	}
}
